/* eslint-disable react/static-property-placement */
/* eslint-disable max-lines */
import { clsx } from 'clsx';
import { Entity } from 'engine/Entity';
import { EngineContext } from 'engine/components/EngineProvider';
import { AudioManager } from 'engine/lib/AudioManager';
import { V2, addV2, emptyV2, newV2, scaleV2, subtractV2 } from 'engine/lib/V2';
import { ConsoleButton } from 'game/components/ConsoleButton';
import { Message, message } from 'game/components/Message';
import { XIcon } from 'game/icons/XIcon';
import { nonEmpty } from 'game/lib/NonEmpty';
import { createStyle, createStyles } from 'game/lib/createStyles';
import { rand } from 'game/lib/rand';
import { shallowEqual } from 'game/lib/shallowEqual';
import { AnyModule, BotState, Module, ModuleType, SocketDef, SocketType, WireSource, socketDefs } from 'game/state/BotState';
import { colors, fonts } from 'game/styles/theme';
import { EditRobotModal } from 'game/tutorial/EditRobotModal';
import { prefsStorage } from 'game/tutorial/prefs';
import clamp from 'lodash/clamp';
import cloneDeep from 'lodash/cloneDeep';
import times from 'lodash/times';
import React from 'react';
import { BotModule } from './BotModule';
import { AddModule } from './EditBot/AddModule';
import { BotConfig } from './EditBot/BotConfig';
import { InventoryGrid } from './EditBot/InventoryGrid';

/* eslint-disable global-require */
const wireInSounds = nonEmpty([
  new AudioManager(require('game/sfx/wire-in-1.mp3')),
  new AudioManager(require('game/sfx/wire-in-2.mp3')),
]);
const wireOutSounds = nonEmpty([
  new AudioManager(require('game/sfx/wire-out-1.mp3')),
  new AudioManager(require('game/sfx/wire-out-2.mp3')),
]);

const chunkSounds = nonEmpty([
  new AudioManager(require('game/sfx/chunk-in-1.mp3')),
  new AudioManager(require('game/sfx/chunk-in-2.mp3')),
  new AudioManager(require('game/sfx/chunk-in-3.mp3')),
  new AudioManager(require('game/sfx/chunk-out-1.mp3')),
  new AudioManager(require('game/sfx/chunk-out-2.mp3')),
  new AudioManager(require('game/sfx/chunk-out-3.mp3')),
]);
/* eslint-enable global-require */

let hasEditedBot = false;

const toNearest = (n: number, increment: number, offset = 0) => {
  return Math.round((n - offset) / increment) * increment + offset;
};

const wireGroup = createStyle('wireGroup', {
  fill: 'none',
  filter: 'drop-shadow(0 2px 5px rgba(0, 0, 0, 0.5))', // TODO: Not supported for SVGs?
  strokeLinecap: 'round',
  cursor: 'pointer',
});

const classes = createStyles({
  editWindow: {
    display: 'flex',
    height: '100%',
  },
  close: {
    position: 'absolute',
    right: 8,
    top: 8,
  },
  inventoryPanel: {
    background: colors.grey14,
    borderLeft: `1px solid ${colors.grey6}`,
    width: '16.67%',
    maxWidth: 320,
    padding: '70px 20px',
  },
  inventoryScreen: {
    background: colors.screenTeal,
    fill: colors.screenTealLight,
    fontFamily: fonts.vcr,
    textTransform: 'uppercase',
    borderRadius: 20,
    boxShadow: 'inset 0 0 20px rgba(0, 0, 0, 0.5), 1px 1px white',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    '& > *': {
      height: '50%',
      padding: 20,
      paddingBottom: 10,
    },
    '& > * + *': {
      paddingBottom: 20,
      paddingTop: 10,
    },
  },
  inventoryColumn: {
    display: 'flex',
    flexDirection: 'column',
  },
  inventoryFill: {
    flexGrow: 1,
    position: 'relative',
    marginTop: 10,
  },
  inventoryContainer: {
    position: 'absolute',
    width: '100%',
    height: '100%',
  },
  modulesPanel: {
    width: '40%',
    backgroundColor: colors.grey14,
    borderLeft: '1px solid white',
    borderRight: `1px solid ${colors.grey6}`,
    padding: 20,
    position: 'relative',
    '&:before': { // flourish
      content: ' ',
      position: 'absolute',
      left: 0,
      top: '75%',
      width: '100%',
      borderTop: `1px solid ${colors.grey6}`,
      boxShadow: '0 1px white',
    },
  },
  modulesWindow: {
    userSelect: 'none',
    height: '100%',
    '& > svg': {
      background: colors.grey5,
      border: `1px solid ${colors.grey1}`,
      boxShadow: 'inset 0 0 40px rgba(0, 0, 0, 0.75), 1px 1px white',
      position: 'relative',
    },
  },
  resizableText: {
    display: 'block',
  },
  disable: {
    pointerEvents: 'none',
  },

  wire: {
    stroke: '#ccc',
    [`.${wireGroup}:hover > &`]: { stroke: '#eee' },
  },
  wireInstance: {
    stroke: 'red',
    [`.${wireGroup}:hover > &`]: { stroke: '#FF491F' },
  },
  wireItem: {
    stroke: '#61BAC9',
    [`.${wireGroup}:hover > &`]: { stroke: '#A3D8D4' },
  },
  wireCommand: {
    stroke: 'orange',
    [`.${wireGroup}:hover > &`]: { stroke: '#FFCA45' },
  },

  addModule: {
    position: 'absolute',
    left: 40,
    right: 40,
    top: 0,
    bottom: 0,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    zIndex: 1,
  },
  addModuleInner: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    height: '75%',
  },
  underlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    height: '100%',
    background: colors.eggShell,
    opacity: 0.5,
  },

  botConfig: { flexGrow: 1 },
  botConfigInner: {
    background: colors.grey14,
    borderBottom: `1px solid ${colors.grey6}`,
    padding: '10px 20px',
  },

  socketHitbox: {
    cursor: 'pointer',
    opacity: 0,
  },
});

interface EditBotProps {
  bot: Entity<{ state: BotState }>,
  onClose: () => void,
}

const Resizable: React.FC<{ text: string }> = ({ text }) => (
  <svg viewBox="0 0 380 72" className={classes.resizableText}>
    <text fontSize={72} y={72} x="50%" textAnchor="middle">
      <Message text={text} />
    </text>
  </svg>
);

const getSocketDef = (module: AnyModule | undefined, name: string): SocketDef | null => {
  if (!module) return null;
  const moduleSocketDefs = socketDefs[module.type] as Partial<Record<string, SocketDef>>;
  return moduleSocketDefs[name] || null;
};

const offsetV2 = (base = emptyV2, offset = emptyV2) => {
  return addV2([...base], offset);
};

const determineWireType = (
  outputModule: AnyModule | undefined,
  socketName: string,
  bot: BotState,
  visited: AnyModule[] = [],
): number => {
  if (!outputModule || visited.includes(outputModule)) return 0;
  if (outputModule.type === ModuleType.Logic) {
    return Object.values(outputModule.inputs).reduce((acc, wire) => {
      if (!wire) return acc;
      visited.push(outputModule);
      const wireType = determineWireType(
        bot.modules.find(m => m.id === wire.moduleId),
        wire.socketName,
        bot,
        visited,
      );
      return acc | (wireType || 0);
    }, 0);
  }
  const socketDef = getSocketDef(outputModule, socketName);
  return socketDef?.type || 0;
};

const wireClassName: Record<SocketType, string> = {
  [SocketType.Item]: classes.wireItem,
  [SocketType.Instance]: classes.wireInstance,
  [SocketType.Command]: classes.wireCommand,
};

const renderWire = (
  [x1, y1]: V2,
  [x2, y2]: V2,
  type: SocketType,
  disable: boolean,
  groupProps?: React.SVGProps<SVGGElement>,
) => {
  let matchingTypes = (Object.values(SocketType) as number[]).filter(test => {
    return (test as SocketType) & type;
  });
  if (!matchingTypes.length) matchingTypes = [0];
  return (
    <g {...groupProps} className={clsx(wireGroup, { [classes.disable]: disable })}>
      {matchingTypes.map((matchingType, i) => (
        <path
          strokeWidth={16 * (1 / 2 ** i)}
          key={matchingType}
          className={clsx(
            classes.wire,
            wireClassName[matchingType as SocketType],
          )}
          d={`M ${x1} ${y1} C ${x1} ${y1 + 100}, ${x2 - 100} ${y2}, ${x2} ${y2}`}
        />
      ))}
    </g>
  );
};

const MODULE_H = 600;
const MODULE_W = 200;
const GAP_HEIGHT = 10;
const NUM_ROWS = 3;
const TARGET_HEIGHT = MODULE_H * NUM_ROWS + GAP_HEIGHT * (NUM_ROWS - 1);

export class EditBot extends React.Component<EditBotProps> {

  static contextType = EngineContext;

  context!: React.ContextType<typeof EngineContext>;

  grabbing: number | null = null;
  wiringFrom: WireSource | null = null;
  wiringTo: WireSource | null = null;
  wiringReversed = false;
  grabStart = emptyV2;
  modulePositions: Partial<{ [moduleId: number]: V2 }> = {};
  adding = false;
  helping = hasEditedBot ? false : !prefsStorage.get().disableHelp;

  onContextMenu = (e: MouseEvent) => {
    const { state } = this.props.bot.props;
    if (this.wiringTo && this.wiringFrom) {
      e.preventDefault();
      if (!shallowEqual(this.wiringFrom, this.wiringTo)) {
        const module = state.modules.find(m => m.id === this.wiringTo?.moduleId);
        if (module) {
          (module as AnyModule).inputs[this.wiringTo.socketName] = this.wiringFrom;
        }
      }
      this.wiringTo = null;
      this.wiringFrom = null;
    }
  };

  onMouseUp = () => {
    if (!this.grabbing) return;
    const { state } = this.props.bot.props;
    const { viewport } = this.context;
    const module = state.modules.find(m => m.id === this.grabbing);
    if (!module) return;
    const pos = this.modulePositions[this.grabbing];
    if (pos) {
      const w = viewport[0] * 0.4 - 40;
      const h = viewport[1] - 40;
      const scale = (h / TARGET_HEIGHT) || 1;
      const realW = w * (1 / scale);
      const x = realW - pos[0];
      module.position[0] = clamp(toNearest(x, MODULE_W / 2) / -MODULE_W, -1);
      module.position[1] = clamp(toNearest(pos[1], MODULE_H) / MODULE_H, 0, 2);
    }
    this.grabbing = null;
    rand(chunkSounds).play();
  };

  onKeyDown = (e: KeyboardEvent) => {
    if (e.code === 'KeyQ') {
      this.adding = !this.adding;
    }
  };

  onGridContextMenu: React.MouseEventHandler = e => {
    e.preventDefault();
    if (this.wiringFrom) return;
    this.adding = true;
  };

  componentDidMount() {
    hasEditedBot = true;
    window.addEventListener('contextmenu', this.onContextMenu);
    window.addEventListener('mouseup', this.onMouseUp);
    window.addEventListener('keydown', this.onKeyDown);
  }

  componentWillUnmount() {
    window.removeEventListener('contextmenu', this.onContextMenu);
    window.removeEventListener('mouseup', this.onMouseUp);
    window.removeEventListener('keydown', this.onKeyDown);
  }

  onClose = () => { this.adding = false; };

  onAddModule = (template: Module, pos: V2) => {
    const { state } = this.props.bot.props;
    const { input, viewport } = this.context;
    const clone = cloneDeep(template);
    const maxId = state.modules.reduce((acc, m) => Math.max(acc, m.id), 0);
    clone.id = maxId + 1;
    state.modules.push(clone);

    const w = viewport[0] * 0.4 - 40;
    const h = viewport[1] - 40;
    const left = 20;
    const scale = (h / TARGET_HEIGHT) || 1;

    const x = w - (pos[0] - left);
    const y = pos[1] - 20;
    clone.position[0] = -x / (MODULE_W * scale);
    clone.position[1] = y / ((MODULE_H + GAP_HEIGHT / 2) * scale);

    this.adding = false;
    this.grabStart = [...input.mouse];
    this.grabbing = clone.id;
    rand(chunkSounds).play();
  };

  render() {
    const { state } = this.props.bot.props;
    const { viewport, input } = this.context;
    const w = viewport[0] * 0.4 - 40;
    const h = viewport[1] - 40;
    const scale = (h / TARGET_HEIGHT) || 1;
    const realW = w * (1 / scale);
    state.modules.forEach(module => {
      this.modulePositions[module.id] = newV2(
        realW + MODULE_W * module.position[0],
        MODULE_H * module.position[1] + GAP_HEIGHT * module.position[1],
      );
      if (module.id === this.grabbing) {
        addV2(
          this.modulePositions[module.id] || newV2(),
          scaleV2(
            subtractV2([...input.mouse], this.grabStart),
            1 / scale,
          ),
        ).join(' ');
      }
    });

    const modulesRenderOrder = (): Module[] => {
      const i = this.grabbing ? state.modules.findIndex(m => m.id === this.grabbing) : -1;
      if (i === -1) return state.modules;
      const clone = [...state.modules];
      clone.push(clone.splice(i, 1)[0]);
      return clone;
    };

    return (
      <div className={classes.editWindow}>
        <div className={classes.modulesPanel}>
          <div className={classes.modulesWindow}>
            <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`}>
              <defs>
                <pattern
                  id="gridPattern"
                  patternUnits="userSpaceOnUse"
                  width={100}
                  height={100}
                >
                  <line x1={100} x2={100} y2={100} stroke={colors.grey1} />
                  <line y1={100} x2={100} y2={100} stroke={colors.grey1} />
                </pattern>
              </defs>
              <g transform={`scale(${scale})`}>
                {times(3, i => (
                  <g key={i} transform={`translate(0 ${MODULE_H * i + (i * GAP_HEIGHT)})`}>
                    <rect
                      width={realW}
                      height={GAP_HEIGHT}
                      y={-GAP_HEIGHT}
                      fill={colors.grey1}
                    />
                    <rect
                      fill="url(#gridPattern)"
                      width={realW}
                      height={MODULE_H}
                      transform="rotate(180)"
                      transform-origin="center"
                      style={{ transformBox: 'fill-box' }}
                      onContextMenu={this.onGridContextMenu}
                    />
                  </g>
                ))}
                {modulesRenderOrder().map(module => (
                  <g
                    className={clsx({ [classes.disable]: this.wiringFrom })}
                    key={module.id}
                    transform={`translate(${this.modulePositions[module.id]?.join(' ')})`}
                    onContextMenu={e => {
                      if (module.type === ModuleType.Cpu
                        || module.type === ModuleType.Door
                        || module.type === ModuleType.Scanner) return;
                      if (this.wiringFrom) return;
                      e.preventDefault();
                      const i = state.modules.indexOf(module);
                      if (i === -1) return;
                      state.modules.splice(i, 1);
                      delete state.cache[module.id];
                      state.modules.forEach(m => {
                        Object.entries(m.inputs).forEach(([inputName, wire]) => {
                          if (wire.moduleId === module.id) {
                            delete (m as AnyModule).inputs[inputName];
                          }
                        });
                      });
                      rand(chunkSounds).play();
                    }}
                  >
                    <BotModule
                      module={module}
                      bot={this.props.bot}
                      grabbing={this.grabbing === module.id}
                      onGrabStart={e => {
                        if (e.button !== 0) return;
                        e.preventDefault();
                        this.grabbing = module.id;
                        this.grabStart = [e.clientX, e.clientY];
                        rand(chunkSounds).play();
                      }}
                    />
                  </g>
                ))}
                {state.modules.map(module => (
                  Object.entries(module.inputs).map(([name, wire]) => {
                    const outModule = state.modules.find(m => m.id === wire.moduleId);
                    const outSocket = getSocketDef(outModule, wire.socketName);
                    const outV2 = offsetV2(
                      this.modulePositions[wire.moduleId],
                      outSocket?.position,
                    );
                    const inSocketDef = getSocketDef(module, name);
                    if (!outV2 || !inSocketDef || !outSocket) return;
                    const inV2 = offsetV2(
                      this.modulePositions[module.id],
                      inSocketDef.position,
                    );
                    const socketType = determineWireType(outModule, wire.socketName, state);
                    return renderWire(outV2, inV2, socketType, !!this.wiringTo, {
                      key: `${module.id}_${name}`,
                      onClick: () => {
                        delete (module as AnyModule).inputs[name];
                      },
                      onContextMenu: e => {
                        e.preventDefault();
                        delete (module as AnyModule).inputs[name];
                      },
                    });
                  })
                ))}
                {this.wiringTo && this.wiringFrom && (
                  (() => {
                    const inSocketDef = getSocketDef(
                      state.modules.find(m => m.id === this.wiringTo?.moduleId),
                      this.wiringTo.socketName,
                    );
                    const inV2 = offsetV2(
                      this.modulePositions[this.wiringTo.moduleId],
                      inSocketDef?.position,
                    );
                    const outModule = state.modules.find(m => m.id === this.wiringFrom?.moduleId);
                    const outSocketDef = getSocketDef(outModule, this.wiringFrom.socketName);
                    const outV2 = offsetV2(
                      this.modulePositions[this.wiringFrom.moduleId],
                      outSocketDef?.position,
                    );
                    if (!outSocketDef) return;
                    const socketType = determineWireType(outModule, this.wiringFrom.socketName, state);
                    if (this.wiringReversed) {
                      return renderWire(
                        addV2(
                          [...outV2],
                          scaleV2(
                            subtractV2([...input.mouse], this.grabStart),
                            1 / scale,
                          ),
                        ),
                        inV2,
                        socketType,
                        true,
                      );
                    }
                    return renderWire(
                      outV2,
                      addV2(
                        [...inV2],
                        scaleV2(
                          subtractV2([...input.mouse], this.grabStart),
                          1 / scale,
                        ),
                      ),
                      socketType,
                      true,
                    );
                  })()
                )}
                {state.modules.map(module => (
                  Object.entries(socketDefs[module.type]).map(([name, socketDef]) => (
                    <circle
                      key={name}
                      cx={this.modulePositions[module.id]![0] + socketDef.position[0]}
                      cy={this.modulePositions[module.id]![1] + socketDef.position[1]}
                      r={20}
                      className={classes.socketHitbox}
                      onClick={e => {
                        if (this.wiringTo && this.wiringFrom) {
                          const dest = this.wiringReversed ? this.wiringTo : this.wiringFrom;
                          const destSocket = getSocketDef(
                            state.modules.find(m => m.id === dest?.moduleId),
                            dest.socketName,
                          );
                          if (!destSocket || destSocket.direction === socketDef.direction
                            || !(destSocket.type & socketDef.type)
                            || module.id === dest.moduleId) {
                            return;
                          }
                          if (socketDef.direction === 'in') {
                            (module as AnyModule).inputs[name] = dest;
                          } else {
                            state.modules.forEach(m => {
                              Object.entries(m.inputs).forEach(([n, wire]) => {
                                if (wire.moduleId === module.id && wire.socketName === name) {
                                  delete (m as AnyModule).inputs[n];
                                }
                              });
                            });
                            const fromModule: AnyModule | undefined = state.modules.find(
                              m => m.id === dest?.moduleId,
                            );
                            if (!fromModule) return;
                            fromModule.inputs[dest.socketName] = {
                              moduleId: module.id,
                              socketName: name,
                            };
                          }
                          this.wiringFrom = null;
                          this.wiringTo = null;
                          rand(wireInSounds).play();
                          return;
                        }
                        rand(wireInSounds).play();
                        this.grabStart = [e.clientX, e.clientY];
                        this.wiringFrom = { moduleId: module.id, socketName: name };
                        this.wiringTo = { moduleId: module.id, socketName: name };
                        this.wiringReversed = socketDef.direction === 'in';
                        let occupied = false;
                        if (socketDef.direction === 'in') {
                          const source = (module as AnyModule).inputs[name];
                          if (source) {
                            occupied = true;
                            this.wiringFrom = source;
                            this.wiringReversed = false;
                          }
                          delete (module as AnyModule).inputs[name];
                        } else {
                          occupied = state.modules.some(m => {
                            if (m.id === module.id) return;
                            return Object.entries(m.inputs).some(([socketName, wire]) => {
                              if (wire.moduleId === module.id) {
                                this.wiringTo = { moduleId: m.id, socketName };
                                this.wiringReversed = true;
                                delete (m as AnyModule).inputs[socketName];
                                return true;
                              }
                            });
                          });
                        }
                        rand(occupied ? wireOutSounds : wireInSounds).play();
                      }}
                    />
                  ))
                ))}
              </g>
            </svg>
          </div>
          {this.adding && (
            <div className={classes.addModule}>
              <div className={classes.addModuleInner}>
                <AddModule
                  bot={this.props.bot}
                  modulesScale={scale}
                  onClose={this.onClose}
                  onAddModule={this.onAddModule}
                />
              </div>
            </div>
          )}
        </div>
        <div className={classes.botConfig}>
          <div className={classes.botConfigInner}>
            <BotConfig
              adding={this.adding}
              setAdding={adding => { this.adding = adding; }}
              helping={this.helping}
              setHelping={helping => { this.helping = helping; }}
              bot={state}
            />
          </div>
        </div>
        <div className={classes.inventoryPanel}>
          <div className={classes.inventoryScreen}>
            <div className={classes.inventoryColumn}>
              <Resizable text="Inventory" />
              <Resizable text="(bot)" />
              <div className={classes.inventoryFill}>
                <div className={classes.inventoryContainer}>
                  <InventoryGrid items={state.inventory} />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className={classes.close}>
          <ConsoleButton
            size="small"
            variant="red"
            title={message('Close (e)')}
            onClick={this.props.onClose}
          >
            <XIcon fill={colors.white} width={20} height={20} />
          </ConsoleButton>
        </div>
        {this.adding && (
          <div
            className={classes.underlay}
            style={{ width: viewport[0] * 0.4 }}
          />
        )}
        {this.helping && (
          <EditRobotModal onClose={() => { this.helping = false; }} />
        )}
      </div>
    );
  }
}
